import { useEffect, useState } from 'react';

export const COLOR_TOKENS = [
  'color-bg-page',
  'color-bg-surface',
  'color-bg-elevated',
  'color-bg-muted',
  'color-bg-subtle',

  'color-text-primary',
  'color-text-secondary',
  'color-text-tertiary',
  'color-text-muted',
  'color-text-disabled',
  'color-text-on-action',
  'color-text-heading',

  'color-border-default',
  'color-border-strong',
  'color-border-focus',

  'color-action-primary',
  'color-action-primary-active',
  'color-action-primary-hover',
  'color-action-primary-bg',

  'color-action-secondary',
  'color-action-secondary-hover',
  'color-action-secondary-active',
  'color-action-secondary-bg',

  'color-action-tertiary',
  'color-action-tertiary-hover',
  'color-action-tertiary-active',
  'color-action-tertiary-bg',

  'color-status-success',
  'color-status-success-bg',
  'color-status-danger',
  'color-status-danger-bg',
  'color-status-warning',
  'color-status-warning-bg',
  'color-status-info',
  'color-status-info-bg',
  'color-status-neutral',
  'color-status-neutral-bg',
] as const;

export type ColorToken = (typeof COLOR_TOKENS)[number];
export type ColorMap = Record<ColorToken, string>;

let resolverEl: HTMLSpanElement | null = null;

function getResolver(): HTMLSpanElement {
  if (resolverEl && resolverEl.isConnected) return resolverEl;
  const el = document.createElement('span');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = 'position:absolute;width:0;height:0;visibility:hidden;pointer-events:none;';
  document.body.appendChild(el);
  resolverEl = el;
  return el;
}

export function getColor(token: ColorToken): string {
  const el = getResolver();
  el.style.color = `var(--${token})`;
  return getComputedStyle(el).color;
}

export function getColors(): ColorMap {
  const out = {} as ColorMap;
  for (const t of COLOR_TOKENS) out[t] = getColor(t);
  return out;
}

type Listener = (colors: ColorMap) => void;
const listeners = new Set<Listener>();
let initialized = false;

function notify(): void {
  if (listeners.size === 0) return;
  const colors = getColors();
  for (const l of listeners) l(colors);
}

function init(): void {
  if (initialized) return;
  initialized = true;
  window.addEventListener('theme-preference-change', notify);
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', notify);
}

export function subscribeColors(listener: Listener): () => void {
  init();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useColors(): ColorMap {
  const [colors, setColors] = useState<ColorMap>(getColors);
  useEffect(() => subscribeColors(setColors), []);
  return colors;
}
